import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

function money(value) {
  return Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function twoDecimals(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
}

const RevenueForecasting = ({ projectVuid, selectedPeriod, periodLabel }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [pcos, setPcos] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [editingAmountId, setEditingAmountId] = useState(null);

  const fetchForecast = async () => {
    if (!projectVuid || !selectedPeriod) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/projects/${projectVuid}/revenue-forecast`, {
        params: { accounting_period_vuid: selectedPeriod },
      });
      setData(response.data);
      setPcos(
        (response.data.pending_pcos || []).map((pco) => ({
          ...pco,
          included_amount: Number(twoDecimals(pco.included_amount ?? pco.procore_amount)),
          procore_amount: Number(twoDecimals(pco.procore_amount)),
        }))
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading revenue forecast');
      setData(null);
      setPcos([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    if (!projectVuid || !selectedPeriod) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/projects/${projectVuid}/revenue-forecast/history`, {
        params: { accounting_period_vuid: selectedPeriod },
      });
      setHistory(response.data || []);
    } catch (err) {
      console.error('Error loading revenue forecast history:', err);
      setHistory([]);
    }
  };

  useEffect(() => {
    fetchForecast();
    setShowHistory(false);
    setHistory([]);
  }, [projectVuid, selectedPeriod]);

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) fetchHistory();
  };

  const updatePco = (pcoId, updates) => {
    setPcos((current) =>
      current.map((pco) => (pco.procore_pco_id === pcoId ? { ...pco, ...updates } : pco))
    );
  };

  const includedTotal = pcos.reduce(
    (sum, pco) => sum + (pco.is_included ? Number(pco.included_amount || 0) : 0),
    0
  );
  const currentRevenue = data?.current_revenue?.total_contract_amount || 0;
  const forecastedRevenue = currentRevenue + includedTotal;
  const canEdit = Boolean(data?.period_is_open);

  const saveSelections = async () => {
    if (!canEdit) return;
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API_BASE_URL}/api/projects/${projectVuid}/revenue-forecast`, {
        accounting_period_vuid: selectedPeriod,
        selections: pcos.map((pco) => ({
          procore_pco_id: pco.procore_pco_id,
          number: pco.number,
          title: pco.title,
          status: pco.status,
          procore_amount: pco.procore_amount,
          is_included: Boolean(pco.is_included),
          included_amount: pco.is_included ? Number(pco.included_amount || 0) : 0,
        })),
      });
      await fetchForecast();
      if (showHistory) await fetchHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving revenue forecast');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedPeriod) {
    return <div className="text-gray-500">Select an accounting period to view revenue forecasting.</div>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-500">Loading revenue forecast...</div>
      </div>
    );
  }

  return (
    <div>
      {error ? (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      ) : null}
      {data?.procore_error ? (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="text-yellow-800">{data.procore_error}</div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white shadow rounded-lg p-5">
          <div className="text-sm text-gray-500">Current Revenue (WIP)</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{money(currentRevenue)}</div>
          <div className="text-xs text-gray-500 mt-2">
            Contract {money(data?.current_revenue?.original_contract_amount)} + Change Orders {money(data?.current_revenue?.change_orders)}
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-5">
          <div className="text-sm text-gray-500">Included Pending PCOs</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{money(includedTotal)}</div>
          <div className="text-xs text-gray-500 mt-2">
            {pcos.filter((pco) => pco.is_included).length} selected for {periodLabel || 'this period'}
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-5">
          <div className="text-sm text-gray-500">Forecasted Revenue</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{money(forecastedRevenue)}</div>
          <div className="text-xs text-gray-500 mt-2">Current revenue + included PCO amounts</div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Pending Potential Change Orders</h2>
            <p className="text-sm text-gray-600 mt-1">
              Include Procore pending PCOs in this period’s revenue forecast. Selections are saved with {periodLabel || 'this period'} and do not carry into the next period.
            </p>
          </div>
          <div className="flex space-x-3">
            <button type="button" className="btn-claret" onClick={toggleHistory}>
              {showHistory ? 'Hide History' : 'View History'}
            </button>
            <button type="button" className="btn-claret" onClick={fetchForecast}>
              Refresh
            </button>
            <button
              type="button"
              className="btn-claret"
              onClick={saveSelections}
              disabled={saving || !canEdit}
            >
              {saving ? 'Saving...' : 'Save Revenue Forecast'}
            </button>
          </div>
        </div>

        {!canEdit ? (
          <div className="px-6 py-3 bg-gray-50 text-sm text-gray-600 border-b border-gray-200">
            This period is closed. Selections are shown for audit and cannot be changed.
          </div>
        ) : null}

        {pcos.length === 0 ? (
          <div className="px-6 py-10 text-center text-gray-500">
            No pending potential change orders were found in Procore for this project.
          </div>
        ) : (
          <div className="forecasting-table-scroll">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Include</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PCO</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Procore Amount</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount to Include</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pcos.map((pco) => (
                  <tr key={pco.procore_pco_id} className={pco.is_included ? 'bg-gray-50' : ''}>
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={Boolean(pco.is_included)}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updatePco(pco.procore_pco_id, {
                            is_included: event.target.checked,
                            included_amount: Number(twoDecimals(
                              event.target.checked
                                ? (pco.included_amount || pco.procore_amount || 0)
                                : pco.procore_amount
                            )),
                          })
                        }
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{pco.number || pco.procore_pco_id}</div>
                      <div className="text-sm text-gray-500">{pco.title || '—'}</div>
                      {!pco.still_pending_in_procore ? (
                        <div className="text-xs text-amber-700 mt-1">Saved for this period; no longer pending in Procore</div>
                      ) : null}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{pco.status || '—'}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{money(pco.procore_amount)}</td>
                    <td className="px-6 py-4 text-right">
                      <input
                        type="number"
                        step="0.01"
                        className="w-36 border border-gray-300 rounded-md px-2 py-1 text-right text-sm"
                        disabled={!canEdit || !pco.is_included}
                        value={
                          pco.is_included
                            ? (editingAmountId === pco.procore_pco_id
                              ? pco.included_amount
                              : twoDecimals(pco.included_amount))
                            : ''
                        }
                        onFocus={() => setEditingAmountId(pco.procore_pco_id)}
                        onChange={(event) => updatePco(pco.procore_pco_id, { included_amount: event.target.value })}
                        onBlur={(event) => {
                          setEditingAmountId(null);
                          updatePco(pco.procore_pco_id, {
                            included_amount: Number(twoDecimals(event.target.value)),
                          });
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showHistory ? (
        <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Selection History for {periodLabel || 'this period'}</h3>
          </div>
          {history.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">No saved selection history for this period.</div>
          ) : (
            <div className="forecasting-table-scroll">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PCO</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {history.map((event) => (
                    <tr key={event.vuid}>
                      <td className="px-6 py-3 text-sm text-gray-900">
                        {event.saved_at ? new Date(event.saved_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-900">{event.saved_by || '—'}</td>
                      <td className="px-6 py-3 text-sm text-gray-900">{event.number || event.procore_pco_id}</td>
                      <td className="px-6 py-3 text-sm text-gray-900">{event.action}</td>
                      <td className="px-6 py-3 text-right text-sm text-gray-900">
                        {event.new_amount != null ? money(event.new_amount) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default RevenueForecasting;
